import { captureReturnUrl, consumeReturnUrl } from './return-url';

/** The forced-2FA-enrollment wizard route. Every "became authenticated" surface sends an
 *  enrollment-pending user here. */
export const ENROLL_ROUTE = '/auth/enroll';

/**
 * The single source of truth for where a user goes once they become authenticated. Shared by every
 * post-auth surface (the redirect-if-authed guard, the auth-check landing component, and — for their
 * enrollment half — the login watcher and cross-tab sync) so the enrollment-vs-return-url priority
 * can't drift between copies:
 *
 * - **authenticated + enrollmentRequired** → the enrollment wizard, WITHOUT consuming the captured
 *   return URL. `consumeReturnUrl()` reads AND clears the stored deep-link, so consuming it here would
 *   destroy the URL the user should land on AFTER enrolling. Only a fully-authenticated user consumes it.
 * - **authenticated + !enrollmentRequired** → the consumed return URL (falls back to the home page).
 * - **not authenticated** → `null`; the caller decides (a guard stays put / allows, the landing
 *   component routes to `/auth/login`).
 *
 * Returns a URL string (or null). Callers wrap it in `router.parseUrl` / `navigateByUrl` as needed.
 * NOTE the side effect: the non-enrollment branch calls `consumeReturnUrl()`, so invoke this only at
 * the point a fully-authenticated user should actually be navigated.
 *
 * `currentUid` (the just-authenticated user) is forwarded to `consumeReturnUrl` so a deep-link
 * captured by a DIFFERENT user in this tab isn't restored for the new user — pass it from the caller's
 * `Auth`. An anonymous (unstamped) capture is restored regardless.
 */
export function resolvePostAuthTarget(
  authed: boolean,
  enrollmentRequired: boolean,
  currentUid?: number | null,
): string | null {
  if (!authed) {
    return null;
  }
  return enrollmentRequired ? ENROLL_ROUTE : consumeReturnUrl(currentUid);
}

/**
 * Navigate to a resolved post-auth `target`, re-capturing it if the navigation does not land so a
 * consumed return URL is never silently lost.
 *
 * `resolvePostAuthTarget`/`consumeReturnUrl` read-AND-CLEAR the stored deep-link, so by the time we
 * navigate it is already gone. `navigateByUrl` can then fail two ways: a lazy `/app/**` chunk fails to
 * fetch (the promise REJECTS) or a guard cancels the navigation (the promise RESOLVES `false`, e.g. a
 * role guard denies the deep-linked route). Re-capture on BOTH outcomes so a retry — or the
 * redirect-if-authed guard on the next attempt — can still honour it. `captureReturnUrl` no-ops for the
 * '/auth/login' fallback and the non-restorable enrollment route, so it is safe to pass those targets.
 *
 * Shared by the login watcher (LoginComponent) and the auth-check landing component so the navigate-
 * then-recapture failure handling lives in ONE place and the two surfaces can't drift. `ownerUid`
 * stamps the re-captured URL (see {@link captureReturnUrl} owner-scoping). Router is typed structurally
 * (only `navigateByUrl` is used) so this stays a dependency-light util.
 */
export function navigateOrRecapture(
  router: { navigateByUrl(url: string): Promise<boolean> },
  target: string,
  ownerUid?: number | null,
): void {
  const recapture = () => captureReturnUrl(target, ownerUid);
  router.navigateByUrl(target).then((ok) => { if (!ok) { recapture(); } }, recapture);
}
